import React, { useMemo, useState } from 'react';
import { Share2 } from 'lucide-react';
import { formatPct, shortDate } from '../../lib/format';
import { convertedRows } from '../../lib/currency';
import { useSort, SortableTh } from '../SortableTable';
import TableSearch from '../TableSearch';
import { MoneyCell } from './MoneyMode';

// Aggregate rows by one or more group keys. `enriched` is an array of
// { row, cost, mv } produced by convertedRows() — cost/mv already in INR.
const aggregate = (enriched, keyFn) => {
  const out = new Map();
  enriched.forEach(({ row, cost, mv }) => {
    const k = keyFn(row);
    if (!out.has(k)) out.set(k, { cost: 0, marketValue: 0, count: 0 });
    const v = out.get(k);
    v.cost += cost;
    v.marketValue += mv;
    v.count += 1;
  });
  return out;
};

const GroupedComparisonTable = ({ label, snapshots, holdings, currency, groupFn, testId, fxRates = {}, showPct = false }) => {
  const [query, setQuery] = useState('');
  const allKeys = useMemo(() => {
    const keys = new Set();
    snapshots.forEach((s) => {
      const rows = Object.values(holdings[s.id] || {}).flat();
      rows.forEach((r) => keys.add(groupFn(r)));
    });
    return Array.from(keys);
  }, [snapshots, holdings, groupFn]);

  // per-snapshot aggregates (FX applied per source before grouping)
  const perSnap = snapshots.map((s) => {
    const enriched = convertedRows(holdings[s.id], fxRates, s.id);
    const agg = aggregate(enriched, groupFn);
    let totCost = 0, totMv = 0;
    agg.forEach((v) => { totCost += v.cost; totMv += v.marketValue; });
    return { snap: s, agg, totCost, totMv };
  });

  const lastAgg = perSnap[perSnap.length - 1]?.agg || new Map();

  const { sort, onSort, sortRows } = useSort({ key: 'group', dir: 'asc' });
  const filteredKeys = allKeys.filter((k) => !query.trim() || String(k).toLowerCase().includes(query.trim().toLowerCase()));
  const sortedKeys = sortRows(filteredKeys, {
    group: (k) => k,
    mv: (k) => (lastAgg.get(k) || { marketValue: 0 }).marketValue,
  });

  const colsPerSnap = showPct ? 5 : 3;
  const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

  return (
    <div>
      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder={`Search ${label.toLowerCase()}…`}
        testId={`search-${testId}`}
        stats={query ? `${filteredKeys.length} / ${allKeys.length}` : null}
      />
      <div className="overflow-x-auto border border-border bg-white">
        <table className="grid-table" data-testid={testId}>
        <thead>
          <tr>
            <SortableTh sortKey="group" sort={sort} onSort={onSort} rowSpan={2} className="min-w-[220px]">{label}</SortableTh>
            {perSnap.map(({ snap }) => (
              <th key={snap.id} colSpan={colsPerSnap} className="text-center">
                <div className="inline-flex items-center gap-1 justify-center">
                  {snap.label}
                  {snap.shared_from && (
                    <span className="chip chip-warning text-[8px] !py-0 !px-1 leading-none" title={`Shared by ${snap.shared_from.email} (${snap.shared_role})`}>
                      <Share2 size={8} /> shared
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-normal text-[#787872] font-mono normal-case tracking-normal mt-1">{shortDate(snap.date)}</div>
              </th>
            ))}
          </tr>
          <tr>
            {perSnap.map(({ snap }, idx) => (
              <React.Fragment key={snap.id}>
                <th className="num">Cost</th>
                {showPct && <th className="num">% Cost</th>}
                {idx === perSnap.length - 1
                  ? <SortableTh sortKey="mv" sort={sort} onSort={onSort} numeric>Mkt Val</SortableTh>
                  : <th className="num">Mkt Val</th>}
                {showPct && <th className="num">% MV</th>}
                <th className="num">P&amp;L %</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedKeys.length === 0 && (
            <tr><td colSpan={1 + perSnap.length * colsPerSnap} className="text-center py-6 text-[#AAA] text-xs">No data for current filters.</td></tr>
          )}
          {sortedKeys.map((k) => (
            <tr key={k}>
              <td className="font-medium">{k}</td>
              {perSnap.map(({ snap, agg, totCost, totMv }) => {
                const v = agg.get(k) || { cost: 0, marketValue: 0 };
                const pnlPct = v.cost > 0 ? ((v.marketValue - v.cost) / v.cost) * 100 : 0;
                return (
                  <React.Fragment key={snap.id}>
                    <td className="num"><MoneyCell value={v.cost} currency={currency} /></td>
                    {showPct && <td className="num text-[#575757]">{formatPct(pct(v.cost, totCost))}</td>}
                    <td className="num"><MoneyCell value={v.marketValue} currency={currency} /></td>
                    {showPct && <td className="num text-[#575757]">{formatPct(pct(v.marketValue, totMv))}</td>}
                    <td className={`num ${pnlPct >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(pnlPct)}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
          <tr style={{ background: '#F6F6F4' }}>
            <td className="font-heading font-bold">TOTAL</td>
            {perSnap.map(({ snap, totCost, totMv }) => {
              const pnlPct = totCost > 0 ? ((totMv - totCost) / totCost) * 100 : 0;
              return (
                <React.Fragment key={snap.id}>
                  <td className="num font-semibold"><MoneyCell value={totCost} currency={currency} /></td>
                  {showPct && <td className="num font-semibold">100%</td>}
                  <td className="num font-semibold"><MoneyCell value={totMv} currency={currency} /></td>
                  {showPct && <td className="num font-semibold">100%</td>}
                  <td className={`num font-semibold ${pnlPct >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(pnlPct)}</td>
                </React.Fragment>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  );
};

export default GroupedComparisonTable;
